import type { LabelEvent, TaskRow, User } from './types';

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = (await res.json()) as { message?: string; error?: string };
      detail = j.message ?? j.error ?? '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

export const apiMe = () => api<{ user: User | null }>('/api/me');

export const apiLogin = (email: string) =>
  api<{ ok: true }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const apiLogout = () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' });

export const apiConsumeLink = (token: string) =>
  api<{ ok: true; redirect: string }>('/api/auth/consume-link', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

export const apiNextEvent = (opts: { task_id?: number; exclude?: number[] } = {}) => {
  const p = new URLSearchParams();
  if (opts.task_id) p.set('task_id', String(opts.task_id));
  if (opts.exclude?.length) p.set('exclude', opts.exclude.join(','));
  const qs = p.toString();
  return api<{ event: LabelEvent | null }>(`/api/events/next${qs ? `?${qs}` : ''}`);
};

export const apiGetEvent = (id: number) =>
  api<{ event: LabelEvent }>(`/api/events/${id}`);

export const apiGetEventByEventId = (eventId: number, taskId?: number) => {
  const qs = taskId ? `?task_id=${taskId}` : '';
  return api<{ event: LabelEvent }>(`/api/events/by-event-id/${eventId}${qs}`);
};

export const apiSubmitLabel = (id: number, data: Record<string, unknown>) =>
  api<{ ok: true }>(`/api/events/${id}/label`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export type EventStatusFilter = 'labeled' | 'unlabelable' | 'unlabeled';
export const apiListEvents = (opts: {
  page?: number;
  page_size?: number;
  q?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  task_id?: number;
  status?: EventStatusFilter;
}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  return api<{
    total: number;
    page: number;
    page_size: number;
    items: LabelEvent[];
  }>(`/api/events?${p.toString()}`);
};

export const apiDeleteLabel = (id: number) =>
  api<{ ok: true }>(`/api/labels/${id}`, { method: 'DELETE' });

export interface LabelHistoryItem {
  id: number;
  label_type: string;
  data: import('./types').LabelData;
  created_at: string;
  deleted_at: string | null;
  labeler_email: string | null;
  status: 'active' | 'deleted';
}
export const apiListEventLabels = (eventPk: number) =>
  api<{ labels: LabelHistoryItem[] }>(`/api/events/${eventPk}/labels`);

export const apiRestoreLabel = (labelId: number) =>
  api<{ ok: true }>(`/api/labels/${labelId}/restore`, { method: 'POST' });

export const apiListTasks = () => api<{ tasks: TaskRow[] }>('/api/tasks');
export const apiListAdminTasks = () => api<{ tasks: TaskRow[] }>('/api/admin/tasks');

export const apiCreateTask = (payload: {
  name: string;
  description?: string;
  events: { event_id: number; video_url: string; [k: string]: unknown }[];
}) =>
  api<{ ok: true; task_id: number; inserted: number; submitted: number }>(
    '/api/admin/tasks',
    { method: 'POST', body: JSON.stringify(payload) },
  );

export const apiPauseTask = (taskId: number) =>
  api<{ ok: true }>(`/api/admin/tasks/${taskId}/pause`, { method: 'POST' });

export const apiResumeTask = (taskId: number) =>
  api<{ ok: true }>(`/api/admin/tasks/${taskId}/resume`, { method: 'POST' });

export const apiExportTaskUrl = (taskId: number) =>
  `/api/admin/tasks/${taskId}/export`;

export interface AdminUserRow {
  email: string;
  is_admin: boolean;
  source: 'env' | 'db';
  created_at: string | null;
  last_login: string | null;
  label_count: number;
  active_sessions: number;
  pending_links: number;
}
export const apiListAdminUsers = () =>
  api<{ users: AdminUserRow[] }>('/api/admin/users');

export const apiAddUser = (email: string) =>
  api<{ ok: true; email: string; is_admin: boolean }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const apiDeleteUser = (email: string) =>
  api<{ ok: true }>(`/api/admin/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });

export const apiRevokeUserSessions = (email: string) =>
  api<{ ok: true; deleted: number }>(
    `/api/admin/users/${encodeURIComponent(email)}/sessions`,
    { method: 'DELETE' },
  );

export const apiCreateMagicLink = (email: string) =>
  api<{ email: string; link: string; expires_at: string; generated_by: string }>(
    '/api/admin/magic-link',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    },
  );
